import { getDeliveryLabel, getPaymentState } from './paymentStatus';

export const ORDER_TABLE_SORT_COLUMNS = Object.freeze([
  { key: 'order', label: 'Order date' },
  { key: 'vendor', label: 'Vendor' },
  { key: 'source', label: 'Source' },
  { key: 'customer', label: 'Customer' },
  { key: 'amount', label: 'Amount' },
  { key: 'payment', label: 'Payment' },
  { key: 'delivery', label: 'Delivery' },
]);

/* ── Who sold this order ────────────────────────────────────────────────────
 * AUDITED 27 Sep 2026 on order 6ab87a8c07d7ea2ae7b1199b, which the orders
 * table called "N/A" while the order detail page called the same line
 * "In-house".
 *
 * Neither was a fact. A vendor's name can live in FOUR places, and the two
 * screens were each populating a different half:
 *
 *   order.brandId.name                brand orders
 *   orderItems[].vendorId             the seller as a User
 *     .businessName / .name
 *   orderItems[]._id.creator          the listing's owner
 *     .businessName / .name
 *   orderItems[]._id.vendorName       denormalised onto the listing
 *   orderItems[].vendorName           denormalised onto the order line
 *
 * The list query populated vendorId and brandId; the detail query populated
 * the listing and its creator, and its creator select even omitted `name`.
 * So each screen asked for one half of the answer and printed a different
 * guess when its half came back empty. Both queries now populate all of it
 * (controllers/orderController.js), and this is the one chain that reads them.
 *
 * WHY THE FALLBACK IS NOT "In-house". `orderItems[].vendorId` is
 * `required: true` on models/Orders.js, so every line HAS a vendor reference.
 * When one exists and no name resolves, the honest statement is that the
 * vendor could not be named — a broken reference, a seller with no name set,
 * or an id pointing outside Users. Printing "In-house" there asserts that
 * Lusuku owns the stock, which is a claim about money and settlement, not a
 * display default. "N/A" was the opposite error: it reads as "no vendor",
 * which is impossible on this schema.
 * ─────────────────────────────────────────────────────────────────────────── */

const textOf = (value) => {
  const s = typeof value === 'string' ? value.trim() : '';
  return s || null;
};

const nameOfUser = (user) =>
  user && typeof user === 'object'
    ? textOf(user.businessName) || textOf(user.name)
    : null;

/**
 * @returns {{label: string, resolved: boolean, hasVendorRef: boolean, title?: string}}
 */
export const resolveOrderVendor = (order) => {
  if (order?.isPackageDelivery) {
    return { label: 'Package Delivery', resolved: true, hasVendorRef: false };
  }

  const item = order?.orderItems?.[0];
  const listing = item && typeof item._id === 'object' ? item._id : null;

  const brand =
    typeof order?.brandId === 'object'
      ? textOf(order.brandId?.name) || textOf(order.brandId?.businessName)
      : null;

  const name =
    brand ||
    nameOfUser(typeof item?.vendorId === 'object' ? item.vendorId : null) ||
    nameOfUser(listing?.creator) ||
    textOf(listing?.vendorName) ||
    textOf(item?.vendorName);

  if (name) return { label: name, resolved: true, hasVendorRef: true };

  // Is there a reference at all? An unpopulated ObjectId still counts — it
  // means a vendor exists that this payload simply did not resolve.
  const hasVendorRef = Boolean(
    item?.vendorId || listing?.creator || order?.brandId || order?.vendorId
  );

  return hasVendorRef
    ? {
        label: 'Unnamed vendor',
        resolved: false,
        hasVendorRef: true,
        title:
          'This order has a vendor, but no business or personal name could be resolved for it. Open the order to check the seller record.',
      }
    : {
        label: 'In-house',
        resolved: true,
        hasVendorRef: false,
        title: 'Sold by Lusuku — this line carries no vendor reference.',
      };
};

/** String form, for sorting and for callers that only want the text. */
export const getOrderVendorLabel = (order) => resolveOrderVendor(order).label;

/* ── What KIND of vendor sold this order ───────────────────────────────────
 * AUDITED 13 Oct 2026 on SWT's POS orders, which the orders table showed
 * with no type pill while the Yalelo in-house order beside them said BRAND.
 *
 * The type can be stated in three places, and the table read them in the
 * wrong order: it took the populated seller's `operationMode` first, which
 * for a brand owner is "BRAND_OWNER", a value no badge map carries, and
 * never fell through to the line's own `vendorType`. A POS brand sale is
 * keyed on the OWNER (controllers/orderController.js, getBrandVendorOrders,
 * "B1"), so every one of them hit exactly that gap.
 *
 * Order of truth: the line's own `vendorType` (the order's statement, set at
 * create), then the seller account's `operationMode` normalised to the type
 * vocabulary, then the top-level brand flags. Always one of the six types
 * the filters and badges know, or null.
 * ─────────────────────────────────────────────────────────────────────────── */
export const ORDER_VENDOR_TYPES = Object.freeze(['RESTAURANT', 'RETAILER', 'WHOLESALER', 'HEALTHCARE', 'BRAND', 'EVENT']);

const OPERATION_MODE_TO_TYPE = {
  RESTAURANT: 'RESTAURANT',
  RETAILER: 'RETAILER',
  WHOLESALER: 'WHOLESALER',
  // A shop account that sells online is a retailer to the order table.
  ONLINE: 'RETAILER',
  HEALTHCARE: 'HEALTHCARE',
  BRAND_OWNER: 'BRAND',
  EVENT_ORGANIZER: 'EVENT',
};

const asType = (value) => {
  const key = String(value || '').trim().toUpperCase();
  if (!key) return null;
  if (ORDER_VENDOR_TYPES.includes(key)) return key;
  return OPERATION_MODE_TO_TYPE[key] || null;
};

/** @returns {string|null} one of ORDER_VENDOR_TYPES, or null when unknown */
export const resolveOrderVendorType = (order) => {
  if (order?.isBrandOrder === true || (order?.brandId && typeof order.brandId === 'object')) return 'BRAND';
  const items = Array.isArray(order?.orderItems) ? order.orderItems : [];
  for (const item of items) {
    const own = asType(item?.vendorType);
    if (own) return own;
  }
  for (const item of items) {
    const seller = item?.vendorId && typeof item.vendorId === 'object' ? item.vendorId : null;
    const fromSeller = asType(seller?.operationMode);
    if (fromSeller) return fromSeller;
    // A Brand document populated in place of a User: a name, no account mode.
    if (seller && seller.name && !seller.operationMode && !seller.businessName) return 'BRAND';
  }
  if (items.some((item) => item?.productId)) return 'BRAND';
  return null;
};

/**
 * EVERY vendor on the order, one entry per distinct seller, in line order.
 * resolveOrderVendor answers for the first line only, which is right for a
 * table cell; an order detail page on a multivendor platform has to name all
 * of them, and the dashboard's admin order page named none. Each entry is a
 * resolveOrderVendor result, so "Unnamed vendor" and "In-house" keep their
 * tooltips.
 */
export const resolveOrderVendors = (order) => {
  const items = Array.isArray(order?.orderItems) ? order.orderItems : [];
  if (items.length === 0) return [resolveOrderVendor(order)];
  const seen = new Set();
  const vendors = [];
  items.forEach((item) => {
    const vendor = resolveOrderVendor({ ...order, orderItems: [item] });
    const key = `${vendor.label}|${vendor.hasVendorRef}`;
    if (seen.has(key)) return;
    seen.add(key);
    vendors.push(vendor);
  });
  return vendors;
};

const timestampFor = (value) => {
  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
};

export const getOrderSortValue = (order, key) => {
  switch (key) {
    case 'order':
      return timestampFor(order?.createdAt);
    case 'vendor':
      return getOrderVendorLabel(order);
    case 'source':
      return order?.orderSrc || order?.source || order?.orderSource || 'Mobile';
    case 'customer':
      return order?.client_id?.name || order?.customerName || '';
    case 'amount':
      return Number(order?.totalPrice || order?.itemsPrice || 0);
    case 'payment':
      return getPaymentState(order);
    case 'delivery':
      return getDeliveryLabel(order?.delivery_status || 'PENDING');
    default:
      return '';
  }
};

const compareValues = (left, right) => {
  if (typeof left === 'number' && typeof right === 'number') {
    return left - right;
  }

  return String(left).localeCompare(String(right), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
};

/**
 * Stable sort for the currently displayed result set. Cloning first is
 * important: mobileOrders belongs to Redux and must never be mutated in-place.
 */
export const sortOrderRows = (orders, { key = 'order', direction = 'desc' } = {}) => {
  if (!Array.isArray(orders)) return [];

  const multiplier = direction === 'asc' ? 1 : -1;
  return orders
    .map((order, originalIndex) => ({ order, originalIndex }))
    .sort((left, right) => {
      const compared = compareValues(
        getOrderSortValue(left.order, key),
        getOrderSortValue(right.order, key)
      );
      return compared === 0
        ? left.originalIndex - right.originalIndex
        : compared * multiplier;
    })
    .map(({ order }) => order);
};
